import {useEffect, useRef, useState} from 'react'

import type {UploaderFile} from './OnboardingUploader'

/**
 * The steps an upload bar shows: 0% when the upload starts, then 10%, 40%, 60% and 100% as the
 * transfer gets there.
 */
export const UPLOAD_STEPS = [0, 0.1, 0.4, 0.6, 1] as const
/** Each step stays on screen at least this long, so a file sent in a blink still shows them all. */
export const UPLOAD_STEP_MS = 400

interface Tracked {
  /** Index in UPLOAD_STEPS of the step on screen. */
  step: number
  /** When that step was shown (performance.now()). */
  since: number
  /** 0..1 actually sent (1 once the page moved on). */
  target: number
  /** The transfer is over: the remaining steps are shown, then the row shows the page's state. */
  finishing: boolean
}

/**
 * Upload bars that move in steps (QA 13 Oct, #8): 0%, then 10%, 40%, 60% and 100%. A step is
 * shown only once the real transfer has reached it, and each stays at least `UPLOAD_STEP_MS`, so a
 * file sent in a blink still goes 0 → 10 → 40 → 60 → 100 instead of jumping, and a row reads
 * "Reading…" only after 100% was shown. A failed upload shows its error at once.
 *
 * @returns The percentage (0..1) to show per file id, for the rows still showing an upload.
 */
export const useGradualUploadProgress = (
  files: readonly UploaderFile[]
): Readonly<Record<string, number>> => {
  const tracked = useRef(new Map<string, Tracked>())
  const [shown, setShown] = useState<Record<string, number>>({})
  const [animating, setAnimating] = useState(false)

  // After each render: follow the page's real states. An upload starts a bar at 0%; its
  // progress raises the target; when the page moves on (reading, read) the bar finishes its steps.
  useEffect(() => {
    const map = tracked.current
    const ids = new Set(files.map((file) => file.id))
    map.forEach((_, id) => {
      if (!ids.has(id)) map.delete(id)
    })
    files.forEach((file) => {
      const entry = map.get(file.id)
      if (file.state === 'upload') {
        const target = Math.max(0, Math.min(1, file.progress ?? 0))
        if (!entry) map.set(file.id, {step: 0, since: performance.now(), target, finishing: false})
        else {
          entry.target = Math.max(entry.target, target)
          entry.finishing = false
        }
        return
      }
      if (!entry || entry.finishing) return
      if (file.state === 'error') map.delete(file.id)
      else {
        entry.target = 1
        entry.finishing = true
      }
    })
    const next = map.size > 0
    if (next !== animating) setAnimating(next)
    if (!next && Object.keys(shown).length > 0) setShown({})
  })

  // One animation frame loop while any bar is on screen.
  useEffect(() => {
    if (!animating) return
    let frame = 0
    const step = (now: number) => {
      const map = tracked.current
      map.forEach((entry, id) => {
        if (now - entry.since < UPLOAD_STEP_MS) return
        const last = UPLOAD_STEPS.length - 1
        if (entry.step < last && entry.target >= UPLOAD_STEPS[entry.step + 1]) {
          entry.step += 1
          entry.since = now
        } else if (entry.step === last && entry.finishing) {
          // 100% was on screen for a moment: the row shows what the page says now.
          map.delete(id)
        }
      })
      const next = Object.fromEntries(
        Array.from(map, ([id, entry]) => [id, UPLOAD_STEPS[entry.step]])
      )
      // Only a changed step re-renders the rows.
      setShown((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next))
      if (map.size > 0) frame = requestAnimationFrame(step)
      else setAnimating(false)
    }
    frame = requestAnimationFrame(step)
    return () => cancelAnimationFrame(frame)
  }, [animating])

  return shown
}
